/**
 * DOM core of the guide. No UI-library knowledge; the drawer takes part only
 * through the `onYield` callback.
 */
import type { HelpStep } from "./types";

/** Everything the guide renders carries this attribute. */
export const HELP_PANEL_ATTR = "data-help-panel";

const OUTLINE_MS = 1800;
const YIELD_MS = 2050;

/** The one definition of "on screen". */
export function isRendered(el: Element): boolean {
  if (el.getClientRects().length === 0) return false;
  const style = window.getComputedStyle(el);
  return style.visibility !== "hidden" && style.display !== "none";
}

export function findStepElement(
  selector: string,
  scope: ParentNode = document
): HTMLElement | null {
  let nodes: NodeListOf<HTMLElement>;
  try {
    nodes = scope.querySelectorAll<HTMLElement>(selector);
  } catch {
    return null;
  }
  for (const node of Array.from(nodes)) {
    if (!node.closest(`[${HELP_PANEL_ATTR}]`) && isRendered(node)) return node;
  }
  return null;
}

export function isStepOnScreen(step: HelpStep, scope: ParentNode = document): boolean {
  return findStepElement(step.selector, scope) !== null;
}

/**
 * The user's topmost open dialog: the LAST rendered [role=dialog] /
 * [role=alertdialog] in document order (portals append), excluding the
 * guide's own panels. Fluent v8 Panels are dialogs too.
 */
export function activeDialogSurface(): HTMLElement | null {
  const nodes = Array.from(
    document.querySelectorAll<HTMLElement>('[role="dialog"], [role="alertdialog"]')
  ).filter((el) => !el.closest(`[${HELP_PANEL_ATTR}]`) && isRendered(el));
  return nodes[nodes.length - 1] ?? null;
}

/**
 * True when one of the guide's panels overlaps `el` horizontally. Measured on
 * the x axis only: the target is about to be scrolled to the vertical centre,
 * so its current y says nothing.
 */
export function isOccludedByPanel(el: Element): boolean {
  const r = el.getBoundingClientRect();
  return Array.from(document.querySelectorAll(`[${HELP_PANEL_ATTR}]`)).some((panel) => {
    if (panel.contains(el)) return false;
    const p = panel.getBoundingClientRect();
    return p.width > 0 && r.right > p.left && r.left < p.right;
  });
}

// --- outline ---------------------------------------------------------------

let outlineTimer: number | undefined;
let outlined: { el: HTMLElement; outline: string; offset: string } | null = null;

function clearOutline() {
  if (!outlined) return;
  outlined.el.style.outline = outlined.outline;
  outlined.el.style.outlineOffset = outlined.offset;
  outlined = null;
}

function outline(el: HTMLElement) {
  clearOutline();
  window.clearTimeout(outlineTimer);
  outlined = { el, outline: el.style.outline, offset: el.style.outlineOffset };
  el.style.outline = "3px solid #f2a900";
  el.style.outlineOffset = "2px";
  outlineTimer = window.setTimeout(clearOutline, OUTLINE_MS);
}

// --- locate ------------------------------------------------------------------

let yieldUntil = 0;
let yieldTimer: number | undefined;

export interface LocateOptions {
  /** true: a guide panel covers the target, stand aside; false: come back. */
  onYield?: (yielding: boolean) => void;
}

/**
 * Scroll to the step's control and outline it. If a guide panel covers it,
 * ask the panel to stand aside for ~2 s. A locate during a yield extends the
 * yield instead of re-measuring (the panel is off-screen, its rect says
 * nothing). Returns false when the control is not on screen.
 */
export function locateElement(step: HelpStep, { onYield }: LocateOptions = {}): boolean {
  const el = findStepElement(step.selector);
  if (!el) return false;
  el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
  outline(el);

  const yielding = Date.now() < yieldUntil;
  if (onYield && (yielding || isOccludedByPanel(el))) {
    if (!yielding) onYield(true);
    yieldUntil = Date.now() + YIELD_MS;
    window.clearTimeout(yieldTimer);
    yieldTimer = window.setTimeout(() => {
      yieldUntil = 0;
      onYield(false);
    }, YIELD_MS);
  }
  return true;
}

// --- inspector ---------------------------------------------------------------

/**
 * The inspector's lookup: the innermost DOCUMENTED anchor around `target`
 * wins; an anchor without a step is walked through, not stopped at; the
 * guide's own panels resolve to nothing.
 */
export function stepAtElement(
  target: Element | null,
  index: ReadonlyMap<string, HelpStep>
): { step: HelpStep; element: HTMLElement } | null {
  if (!target || target.closest(`[${HELP_PANEL_ATTR}]`)) return null;
  let node: Element | null = target.closest("[data-help]");
  while (node) {
    const step = index.get(node.getAttribute("data-help") ?? "");
    if (step) return { step, element: node as HTMLElement };
    node = node.parentElement?.closest("[data-help]") ?? null;
  }
  return null;
}

// --- "take me there" -----------------------------------------------------------

const POLL_MS = 160;

export interface FlashOptions {
  /** Press the control instead of only outlining it (when it is pressable and enabled). */
  activate?: boolean;
  /** Called right before pressing, so the drawer can get out of the way. */
  onHandover?: () => void;
  /** Called when nothing could be outlined before the deadline. */
  onMissing?: () => void;
  timeoutMs?: number;
}

function isPressable(el: HTMLElement): boolean {
  const pressable = el.matches("button, a, [role=button], [role=tab], [role=menuitem]")
    ? el
    : el.querySelector<HTMLElement>("button, a, [role=button]");
  return !!pressable && !pressable.matches(":disabled, [aria-disabled=true]");
}

/**
 * For a control on a screen that may still be loading: poll until it appears,
 * then outline it — or press it when `activate` is set and exactly one such
 * control is on screen (a per-row action exists once per row; choosing the
 * row is never the guide's call). Always reports: `onMissing` fires whenever
 * nothing was outlined, so a "take me there" can never silently do nothing.
 * Returns a cancel function.
 */
export function flashAnchor(anchor: string, opts: FlashOptions = {}): () => void {
  const { activate, onHandover, onMissing, timeoutMs = 6000 } = opts;
  const selector = `[data-help="${anchor}"]`;
  const deadline = Date.now() + timeoutMs;
  let timer: number | undefined;
  let cancelled = false;

  const tick = () => {
    if (cancelled) return;
    const all = Array.from(document.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => !el.closest(`[${HELP_PANEL_ATTR}]`) && isRendered(el)
    );
    const el = all[0];
    if (el) {
      if (activate && all.length === 1 && isPressable(el)) {
        onHandover?.();
        const target = el.matches("button, a, [role=button]")
          ? el
          : el.querySelector<HTMLElement>("button, a, [role=button]") ?? el;
        window.setTimeout(() => {
          const dialogsBefore = activeDialogSurface();
          target.click();
          // a press that opened nothing degrades to a flash
          window.setTimeout(() => {
            if (activeDialogSurface() === dialogsBefore) {
              el.scrollIntoView({ behavior: "smooth", block: "center" });
              outline(el);
            }
          }, 400);
        }, 120);
      } else {
        onHandover?.();
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
        outline(el);
      }
      return;
    }
    if (Date.now() > deadline) {
      onMissing?.();
      return;
    }
    timer = window.setTimeout(tick, POLL_MS);
  };
  tick();
  return () => {
    cancelled = true;
    window.clearTimeout(timer);
  };
}

// --- reveal: open what a context step lives in -----------------------------------

const REVEAL_WAIT_MS = 2500;

/** Poll `probe` until it returns something or the time is up. */
export function waitFor<T>(probe: () => T | null, timeoutMs: number): Promise<T | null> {
  const deadline = Date.now() + timeoutMs;
  return new Promise((resolve) => {
    const tick = () => {
      const found = probe();
      if (found || Date.now() > deadline) resolve(found);
      else window.setTimeout(tick, POLL_MS);
    };
    tick();
  });
}

function firstEnabled(selector: string): HTMLElement | null {
  let nodes: HTMLElement[];
  try {
    nodes = Array.from(document.querySelectorAll<HTMLElement>(selector));
  } catch {
    return null;
  }
  for (const node of nodes) {
    if (node.closest(`[${HELP_PANEL_ATTR}]`) || !isRendered(node)) continue;
    const target = node.matches("button, a, input, [role=button], [role=tab], [role=menuitem], [data-selection-toggle]")
      ? node
      : node.querySelector<HTMLElement>("button, a, input, [role=button]") ?? node;
    if (!target.matches(":disabled, [aria-disabled=true]")) return target;
  }
  return null;
}

/** Press each selector in order (first enabled match), waiting for each to appear. */
export async function pressInOrder(selectors: readonly string[], timeoutMs = REVEAL_WAIT_MS): Promise<boolean> {
  for (const selector of selectors) {
    const control = await waitFor(() => firstEnabled(selector), timeoutMs);
    if (!control) return false;
    control.click();
  }
  return true;
}

/**
 * Make a context step appear: press each selector of its recipe in order (the
 * first enabled match — any row will do to show a row's menu), stopping as
 * soon as the step is on screen. Resolves true when it is.
 */
export async function revealStep(
  step: HelpStep,
  recipe: readonly string[],
  timeoutMs = REVEAL_WAIT_MS
): Promise<boolean> {
  for (const selector of recipe) {
    if (findStepElement(step.selector)) return true;
    const control = await waitFor(() => firstEnabled(selector), timeoutMs);
    if (!control) return false;
    control.click();
  }
  return (await waitFor(() => findStepElement(step.selector), timeoutMs)) !== null;
}
